import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';

import { RouterOutlet } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';

import { PulseFooterComponent } from '@shared/layout/footer';
import { PulseNavigationComponent, NavigationConfig } from '@shared/layout/navigation';
import { PulseTopbarComponent, PulseHeaderComponent, HeaderConfig, PulseLocaleSetupBannerComponent } from '@shared/layout/header';
import { BaseConfig } from './models/base-config.model';

import { LayoutService, ToasterService } from '@shared/services';
import { CobrowseService } from '@shared/cobrowse';
import { AuthService, SessionTimeoutDialogComponent } from '@shared/auth';
import { PulseLoadingScreenComponent } from '@shared/components/loading-screen';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@shared/config';

import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'pulse-base',
  imports: [
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    MenuModule,
    MessageModule,
    PulseFooterComponent,
    PulseHeaderComponent,
    PulseLoadingScreenComponent,
    PulseNavigationComponent,
    PulseTopbarComponent,
    PulseLocaleSetupBannerComponent,
    RouterOutlet,
    SessionTimeoutDialogComponent,
    ToastModule
  ],
  templateUrl: './pulse-base.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PulseBaseComponent {
  readonly authService = inject(AuthService);
  private envConfig = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);
  private clipboard = inject(Clipboard);
  private toasterService = inject(ToasterService);
  private cobrowseService = inject(CobrowseService);

  layoutService = inject(LayoutService);
  baseConfig = input.required<BaseConfig>();
  navigationConfig = input.required<NavigationConfig>();
  headerConfig = input.required<HeaderConfig>();
  navMenu = input.required<MenuItem[]>();

  supportMenu = signal<MenuItem[]>([]);
  coBrowseCode = signal<string>('');
  loadingToastKey = this.toasterService.getLoadingToastKey();

  constructor() {
    this.supportMenu.set([
      {
        label: $localize`Cobrowse`,
        icon: 'fa-duotone fa-solid fa-eye',
        command: () => this.getCobrowseSessionCode()
      },
      {
        label: $localize`Contact Support`,
        icon: 'fa-duotone fa-solid fa-headset',
        command: () => this.contactSupport()
      }
    ]);
  }

  getCobrowseSessionCode() {
    this.cobrowseService
      .createSessionCode()
      .then((code: string) => {
        this.coBrowseCode.set(code);
        this.layoutService.showCoBrowseDialog();
      })
      .catch(() => {
        this.toasterService.displayError($localize`Failed to retrieve code`, $localize`Please try again`);
      });
  }

  copyCodeToClipboard() {
    this.clipboard.copy(this.coBrowseCode());
    this.toasterService.displaySuccess($localize`Code copied to clipboard`, this.coBrowseCode());
  }

  contactSupport(): void {
    window.location.href = `mailto:${this.envConfig.supportEmailAddress}`;
  }
}
